const CONFIG = window.GS_SAVOR_ANALYTICS_CONFIG || {};
const VERSION = CONFIG.websiteVersion || 'savor_menu_analytics_20260720';
const SHOPIFY_EVENT_PREFIX = 'gs:';
const VIEW_THRESHOLD = 0.35;
const VIEW_DELAY_MS = 600;
const CART_ATTEMPT_WINDOW_MS = 15000;
const RECENT_LIMIT = 80;
const NAVIGATION_EVENT = 'gs_navigation_click';

if (!window.__gsSavorAnalyticsLoaded) {
  window.__gsSavorAnalyticsLoaded = true;

  const EVENT_ALIASES = {
    gs_collection_filter_apply: 'gs_filter_apply',
    gs_collection_filter_clear: 'gs_filter_apply',
    gs_collection_filter_open: 'gs_module_click',
    gs_collection_sort_apply: 'gs_sort_apply',
    gs_faq_expand: 'gs_module_click',
    gs_social_proof_click: 'gs_module_click',
    gs_cross_link_click: 'gs_module_click',
    gs_category_escape_click: 'gs_module_click',
    gs_filter_path_click: 'gs_module_click',
    gs_review_source_click: 'gs_module_click',
    gs_cart_recommendation_click: 'gs_product_card_click',
    gs_quick_add_open: 'gs_module_click',
    gs_quick_add_submit: 'gs_module_click',
    gs_drop_list_submit: 'gs_module_click',
    pdp_bundle_cross_sell_click: 'gs_module_click',
    pdp_mystery_snack_sack_click: 'gs_module_click',
    pdp_deal_module_toggle: 'gs_module_click',
    pdp_deal_module_click: 'gs_module_click',
    pdp_merchandising_path_click: 'gs_module_click',
    pdp_swipe_entry_click: 'gs_swipe_entry_click',
  };

  const IGNORED_CLICK_EVENTS = new Set([
    'gs_page_view',
    'gs_module_view',
    'gs_product_card_view',
  ]);

  const PRODUCT_VIEW_EVENTS = new Set(['gs_module_view', 'gs_product_card_view']);
  const CART_ATTEMPT_EVENTS = new Set(['gs_module_click', 'gs_product_card_click']);
  const RECENT_EVENTS = [];
  const observedViews = new WeakSet();
  const completedViews = new WeakSet();
  const viewTimers = new WeakMap();
  const emittedLeadResults = new WeakSet();
  const recentCartAttempts = [];

  function normalizeEventName(rawName) {
    if (!rawName) return '';
    const name = String(rawName).trim();
    return EVENT_ALIASES[name] || name;
  }

  function isSafeEventName(eventName) {
    return eventName.startsWith('gs_') && eventName !== 'gs_page_view';
  }

  function asElement(value) {
    return value instanceof Element ? value : null;
  }

  function closestWithDataset(start, key) {
    let element = asElement(start);

    while (element) {
      if (element instanceof HTMLElement && Object.prototype.hasOwnProperty.call(element.dataset, key)) {
        return element.dataset[key] || '';
      }

      element = element.parentElement;
    }

    return '';
  }

  function textValue(value) {
    if (value === undefined || value === null) return '';
    return String(value).replace(/\s+/g, ' ').trim();
  }

  function numericValue(value) {
    if (value === undefined || value === null || value === '') return '';
    const number = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(number) ? number : '';
  }

  function gidToId(value) {
    const raw = textValue(value);
    if (!raw) return '';
    return raw.split('/').pop() || raw;
  }

  function safePath(urlValue) {
    const raw = textValue(urlValue);
    if (!raw) return '';

    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.origin !== window.location.origin) return parsed.hostname;
      return parsed.pathname;
    } catch (error) {
      return raw.startsWith('/') ? raw.split('?')[0].split('#')[0] : '';
    }
  }

  function normalizeHandle(value) {
    return textValue(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
  }

  function normalizeSurface(value) {
    const surface = textValue(value);
    if (surface === 'site_header_mega_menu') return 'site_mega_menu';
    if (surface === 'site_mobile_bar' || surface === 'site_nav') return 'site_header';
    return surface;
  }

  function currentPageContext() {
    const templateName = CONFIG.templateName || window.Theme?.template?.name || '';

    return {
      source_feature: 'website_2',
      environment: CONFIG.environment || 'unknown',
      website_version: VERSION,
      page_type: CONFIG.pageType || '',
      template_name: templateName,
      theme_name: CONFIG.themeName || '',
      theme_role: CONFIG.themeRole || '',
      theme_id: CONFIG.themeId || '',
      page_path: window.location.pathname,
      device_type: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
    };
  }

  function collectionContext(target) {
    const collectionSection =
      asElement(target)?.closest('[data-gs-collection-section]') ||
      document.querySelector('[data-gs-collection-section]');

    return {
      collection_handle: collectionSection?.dataset.gsCollectionHandle || '',
      collection_id: collectionSection?.dataset.gsCollectionId || '',
      collection_title: collectionSection?.dataset.gsCollectionTitle || '',
    };
  }

  function elementContext(target) {
    const element = asElement(target);
    const productTitle =
      closestWithDataset(element, 'gsProductTitle') ||
      closestWithDataset(element, 'gsProductDisplayName') ||
      closestWithDataset(element, 'gsProductName');
    const surface = normalizeSurface(closestWithDataset(element, 'gsSurface'));
    const navTitle = closestWithDataset(element, 'gsNavTitle') || element?.closest('a[href]')?.textContent || '';
    const navSection =
      closestWithDataset(element, 'gsNavSection') ||
      closestWithDataset(element, 'gsNavGroup') ||
      (surface === 'site_header' ? 'primary' : '');

    return {
      surface,
      module: closestWithDataset(element, 'gsModule') || closestWithDataset(element, 'gsBlockType'),
      module_id: closestWithDataset(element, 'gsModuleId'),
      click_target: closestWithDataset(element, 'gsClickTarget'),
      position: closestWithDataset(element, 'gsPosition'),
      nav_section: navSection,
      nav_parent: closestWithDataset(element, 'gsNavParent'),
      nav_item: closestWithDataset(element, 'gsNavItem') || normalizeHandle(navTitle),
      nav_title: navTitle,
      nav_depth: closestWithDataset(element, 'gsNavDepth'),
      product_id: closestWithDataset(element, 'gsProductId') || closestWithDataset(element, 'productId'),
      variant_id: closestWithDataset(element, 'gsVariantId'),
      product_handle: closestWithDataset(element, 'gsProductHandle'),
      product_title: productTitle,
      product_display_name: closestWithDataset(element, 'gsProductDisplayName'),
      product_type: closestWithDataset(element, 'gsProductType'),
      brand_family: closestWithDataset(element, 'gsBrandFamily'),
      product_country: closestWithDataset(element, 'gsProductCountry') || closestWithDataset(element, 'gsProductOrigin'),
      origin_country: closestWithDataset(element, 'gsProductOrigin'),
      availability_status: closestWithDataset(element, 'gsAvailabilityStatus'),
      filter_group: closestWithDataset(element, 'gsFilterGroup'),
      filter_value: closestWithDataset(element, 'gsFilterValue'),
      recommendation_strategy:
        closestWithDataset(element, 'gsRecommendationStrategy') ||
        closestWithDataset(element, 'gsRecommendationSource') ||
        closestWithDataset(element, 'gsMatchStrategy'),
      cart_source: closestWithDataset(element, 'gsSource') || closestWithDataset(element, 'gsCartSource'),
      capture_type: closestWithDataset(element, 'gsCaptureType'),
      submission_result: closestWithDataset(element, 'gsSubmissionResult'),
      deal_id: closestWithDataset(element, 'gsDealId'),
      deal_type: closestWithDataset(element, 'gsDealType'),
      deal_group: closestWithDataset(element, 'gsDealGroup'),
      intent_dimension: closestWithDataset(element, 'gsIntentDimension') || closestWithDataset(element, 'gsPathType'),
      intent_value: closestWithDataset(element, 'gsIntentValue') || closestWithDataset(element, 'gsPathValue'),
      target_collection_handle: closestWithDataset(element, 'gsTargetCollectionHandle'),
    };
  }

  function filterContext(target) {
    const shell = asElement(target)?.closest('[data-gs-find-snacks-shell], [data-gs-collection-section]');
    const selectedFilters = shell ? shell.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked') : [];
    const resultCountText =
      shell?.querySelector('[data-gs-filter-count]')?.textContent ||
      shell?.querySelector('.products-count-wrapper [role="status"]')?.textContent ||
      '';

    return {
      filter_count: selectedFilters.length || '',
      result_count: numericValue(resultCountText),
      filter_action: closestWithDataset(target, 'gsFilterAction'),
    };
  }

  function sanitizeValue(key, value) {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'boolean') return value;

    const stringValue = textValue(value);
    if (!stringValue) return undefined;

    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes('email') ||
      lowerKey.includes('phone') ||
      lowerKey.includes('address') ||
      lowerKey.includes('customer') ||
      lowerKey === 'name' ||
      lowerKey.includes('order_number') ||
      lowerKey.includes('order_id') ||
      lowerKey === 'search_term' ||
      lowerKey === 'search_query'
    ) {
      return undefined;
    }

    return stringValue.slice(0, 180);
  }

  function sanitizePayload(payload) {
    const output = {};

    for (const [key, value] of Object.entries(payload)) {
      const sanitized = sanitizeValue(key, value);
      if (sanitized !== undefined) output[key] = sanitized;
    }

    return output;
  }

  function buildPayload(target, details = {}) {
    return sanitizePayload({
      ...currentPageContext(),
      ...collectionContext(target),
      ...elementContext(target),
      ...details,
    });
  }

  function recordRecent(eventName, payload) {
    RECENT_EVENTS.push({
      eventName,
      payload,
      at: new Date().toISOString(),
    });

    while (RECENT_EVENTS.length > RECENT_LIMIT) RECENT_EVENTS.shift();
    document.documentElement.dataset.gsLastEvent = eventName;
  }

  function publish(rawName, target, details = {}) {
    const eventName = normalizeEventName(rawName);
    if (!isSafeEventName(eventName)) return null;

    const payload = buildPayload(target, details);
    recordRecent(eventName, payload);

    try {
      window.Shopify?.analytics?.publish?.(`${SHOPIFY_EVENT_PREFIX}${eventName}`, payload);
    } catch (error) {
      // Analytics must never block shopping behavior.
    }

    window.dispatchEvent(
      new CustomEvent('gs:analytics', {
        detail: { eventName, payload },
      })
    );

    return payload;
  }

  function shouldObserveView(element) {
    const rawName = element.dataset.gsEvent || (element.matches('[data-gs-track-view="true"]') ? 'gs_module_view' : '');
    const eventName = normalizeEventName(rawName);
    if (!PRODUCT_VIEW_EVENTS.has(eventName)) return false;

    const merchandisingShelf = element.closest('[data-gs-merchandising-shelf]');
    if (merchandisingShelf && merchandisingShelf.dataset.gsMerchandisingReady !== 'true') return false;

    if (eventName === 'gs_product_card_view') {
      const surface = closestWithDataset(element, 'gsSurface');
      const module = closestWithDataset(element, 'gsModule');
      return /recommend|shelf|row|bundle|cart|pdp|homepage|curated|deal/.test(`${surface} ${module}`);
    }

    return true;
  }

  function observeViews(root = document) {
    root.querySelectorAll('[data-gs-event="gs_module_view"], [data-gs-event="gs_product_card_view"], [data-gs-track-view="true"]').forEach(
      (element) => {
        if (!(element instanceof HTMLElement)) return;
        if (observedViews.has(element) || !shouldObserveView(element)) return;
        observedViews.add(element);
        viewObserver.observe(element);
      }
    );

    emitRenderedLeadResults(root);
  }

  const viewObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const target = entry.target;
        if (!(target instanceof HTMLElement) || completedViews.has(target)) return;

        if (entry.isIntersecting && entry.intersectionRatio >= VIEW_THRESHOLD) {
          if (viewTimers.has(target)) return;

          const timer = window.setTimeout(() => {
            completedViews.add(target);
            viewTimers.delete(target);
            viewObserver.unobserve(target);
            publish(target.dataset.gsEvent || 'gs_module_view', target, {
              visibility_threshold: VIEW_THRESHOLD,
            });
          }, VIEW_DELAY_MS);

          viewTimers.set(target, timer);
        } else if (viewTimers.has(target)) {
          window.clearTimeout(viewTimers.get(target));
          viewTimers.delete(target);
        }
      });
    },
    { threshold: [0, 0.25, VIEW_THRESHOLD, 0.5, 0.75] }
  );

  function rememberCartAttempt(target, details = {}) {
    const payload = buildPayload(target, details);
    const variantId = payload.variant_id || details.variant_id || '';
    recentCartAttempts.push({
      payload,
      target: asElement(target),
      variantId: gidToId(variantId),
      at: Date.now(),
    });

    while (recentCartAttempts.length > 16) recentCartAttempts.shift();
  }

  function findCartAttempt(eventTarget, lines = []) {
    const now = Date.now();
    const target = asElement(eventTarget);
    const lineVariantIds = lines.map((line) => gidToId(line?.merchandiseId)).filter(Boolean);

    for (let index = recentCartAttempts.length - 1; index >= 0; index -= 1) {
      const attempt = recentCartAttempts[index];
      if (now - attempt.at > CART_ATTEMPT_WINDOW_MS) continue;

      if (lineVariantIds.length && attempt.variantId && lineVariantIds.includes(attempt.variantId)) {
        return attempt.payload;
      }

      if (target && attempt.target && (attempt.target === target || attempt.target.contains(target) || target.contains(attempt.target))) {
        return attempt.payload;
      }
    }

    return {};
  }

  function trackCartAttemptFromForm(form, submitter) {
    if (!(form instanceof HTMLFormElement)) return;

    const isAddForm = form.matches('[data-type="add-to-cart-form"], [data-gs-cart-addon-form]');
    if (!isAddForm) return;

    const variantId = form.querySelector('input[name="id"]')?.value || closestWithDataset(submitter, 'gsVariantId');
    rememberCartAttempt(submitter || form, {
      variant_id: variantId,
      click_target: closestWithDataset(submitter, 'gsClickTarget') || 'add_to_cart_submit',
      cart_source: closestWithDataset(form, 'gsSource') || closestWithDataset(submitter, 'gsSource'),
      attempt_type: 'form_submit',
    });
  }

  function handleTrackedClick(event) {
    const clickTarget = asElement(event.target);
    if (!clickTarget) return;

    const tracked = clickTarget.closest('[data-gs-event], [data-gs-analytics-event]');
    if (tracked instanceof HTMLElement) {
      const rawName = tracked.dataset.gsEvent || tracked.dataset.gsAnalyticsEvent || '';
      const eventName = normalizeEventName(rawName);

      if (IGNORED_CLICK_EVENTS.has(rawName) || IGNORED_CLICK_EVENTS.has(eventName)) return;

      const anchor = tracked instanceof HTMLAnchorElement ? tracked : tracked.closest('a[href]');
      if (eventName === NAVIGATION_EVENT && !(anchor instanceof HTMLAnchorElement)) return;

      const details = {
        target_path: anchor instanceof HTMLAnchorElement ? safePath(anchor.href) : '',
      };

      if (rawName === 'gs_quick_add_submit' || rawName === 'gs_cart_recommendation_click' || eventName === 'gs_product_card_click') {
        rememberCartAttempt(tracked, {
          ...details,
          variant_id: closestWithDataset(tracked, 'gsVariantId'),
          attempt_type: 'click',
        });
      }

      if (CART_ATTEMPT_EVENTS.has(eventName) && closestWithDataset(tracked, 'gsClickTarget') === 'product_quick_add') {
        rememberCartAttempt(tracked, {
          ...details,
          variant_id: closestWithDataset(tracked, 'gsVariantId'),
          attempt_type: 'quick_add_click',
        });
      }

      publish(eventName, tracked, details);
      return;
    }

    const searchLink = clickTarget.closest('.predictive-search-results__card a[href], results-list .product-grid__item a[href*="/products/"]');
    if (searchLink instanceof HTMLAnchorElement) {
      const card = searchLink.closest('[data-product-id], [data-gs-product-id], .predictive-search-results__card, .product-grid__item');
      publish('gs_search_result_click', card || searchLink, {
        surface: searchLink.closest('.predictive-search-results__card') ? 'predictive_search' : 'search_results',
        target_path: safePath(searchLink.href),
      });
    }
  }

  function handleFilterChange(event) {
    const target = asElement(event.target);
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.matches('[data-gs-filter-sort], select[name="sort_by"]')) {
      publish('gs_sort_apply', target, {
        sort_value: target.value,
        sort_label: target.selectedOptions?.[0]?.textContent || '',
        ...filterContext(target),
      });
      return;
    }

    if (target.name?.startsWith('filter.') || target.closest('[data-gs-find-snacks-shell]')) {
      publish('gs_filter_apply', target, {
        filter_group: closestWithDataset(target, 'gsFilterGroup') || target.name,
        filter_value: closestWithDataset(target, 'gsFilterValue') || target.value,
        filter_action: target instanceof HTMLInputElement && !target.checked ? 'remove' : 'apply',
        ...filterContext(target),
      });
    }
  }

  function handleFilterClick(event) {
    const target = asElement(event.target);
    if (!target) return;

    const control = target.closest('[data-gs-filter-apply], [data-gs-filter-clear], [data-gs-filter-reset], .facets__clear, .facets__clear-all, .facets__clear-all-link, facet-remove-component button');
    if (!(control instanceof HTMLElement)) return;

    const action = control.matches('[data-gs-filter-clear], [data-gs-filter-reset], .facets__clear, .facets__clear-all, .facets__clear-all-link, facet-remove-component button')
      ? 'clear'
      : 'apply';

    publish('gs_filter_apply', control, {
      filter_action: action,
      ...filterContext(control),
    });
  }

  function handleDetailsToggle(event) {
    const target = asElement(event.target);
    if (!(target instanceof HTMLDetailsElement) || !target.open) return;

    if (target.matches('[data-gs-faq-item]')) {
      publish('gs_module_click', target, {
        module: 'faq',
        click_target: 'disclosure_open',
        position: target.dataset.gsFaqPosition || '',
      });
      return;
    }
  }

  function handleFormSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) return;
    trackCartAttemptFromForm(event.target, event.submitter);

    const leadRoot = event.target.closest('[data-gs-lead-capture-form]');
    if (leadRoot instanceof HTMLElement) {
      publish('gs_module_click', leadRoot, {
        click_target: 'lead_capture_submit',
        submission_result: 'attempt',
      });
    }
  }

  function handleInvalid(event) {
    const target = asElement(event.target);
    const leadRoot = target?.closest('[data-gs-lead-capture-form]');
    if (!(leadRoot instanceof HTMLElement)) return;

    publish('gs_lead_capture_submit', leadRoot, {
      submission_result: 'client_error',
    });
  }

  function emitRenderedLeadResults(root = document) {
    root.querySelectorAll('[data-gs-lead-capture-form][data-gs-submission-result]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const result = textValue(element.dataset.gsSubmissionResult);
      if (!result || result === 'attempt' || emittedLeadResults.has(element)) return;

      emittedLeadResults.add(element);
      publish('gs_lead_capture_submit', element, {
        submission_result: result,
      });
    });
  }

  function handleCartLinesUpdate(event) {
    const eventLike = event;
    const action = eventLike.action || eventLike.detail?.action;
    if (action !== 'add') return;

    const lines = Array.isArray(eventLike.lines) ? eventLike.lines : eventLike.detail?.lines || [];
    const promise = eventLike.promise || eventLike.detail?.promise;
    if (!promise || typeof promise.then !== 'function') return;

    const attemptPayload = findCartAttempt(event.target, lines);
    const eventTargetContext = buildPayload(event.target, {});

    promise
      .then((result) => {
        const detail = result?.detail || {};
        if (detail.didError) return;

        const firstLine = lines[0] || {};
        const productId = detail.productId || attemptPayload.product_id || eventTargetContext.product_id;
        const variantId = attemptPayload.variant_id || gidToId(firstLine.merchandiseId);
        const quantity = detail.itemCount || firstLine.quantity || attemptPayload.quantity || '';

        publish('gs_product_added_from_surface', event.target, {
          ...attemptPayload,
          product_id: productId,
          variant_id: variantId,
          quantity,
          item_count: detail.itemCount || quantity,
          cart_context: eventLike.context || eventLike.detail?.context || '',
          cart_source: attemptPayload.cart_source || attemptPayload.surface || detail.source || eventTargetContext.surface || 'native_savor',
          cart_result: 'success',
        });
      })
      .catch(() => {});
  }

  function handleCartDrawerOpen(event) {
    const drawer = asElement(event.target);
    if (!drawer || drawer.id !== 'cart-drawer') return;

    publish('gs_cart_drawer_open', drawer, {
      surface: 'cart_drawer',
      module: 'native_cart_drawer',
      cart_source: 'native_savor',
    });
  }

  function sectionRootFromEvent(event) {
    return asElement(event.target) || document;
  }

  document.addEventListener('click', handleFilterClick, true);
  document.addEventListener('click', handleTrackedClick, true);
  document.addEventListener('change', handleFilterChange, true);
  document.addEventListener('toggle', handleDetailsToggle, true);
  document.addEventListener('submit', handleFormSubmit, true);
  document.addEventListener('invalid', handleInvalid, true);
  document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate, true);
  document.addEventListener('theme-drawer:open', handleCartDrawerOpen, true);
  document.addEventListener('shopify:section:load', (event) => observeViews(sectionRootFromEvent(event)));
  document.addEventListener('gs:merchandising:ready', (event) => observeViews(sectionRootFromEvent(event)));

  window.gsAnalytics = {
    publish,
    observe: observeViews,
    recent: RECENT_EVENTS,
    version: VERSION,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeViews(), { once: true });
  } else {
    observeViews();
  }
}
